// Offline outbox for patient document uploads.
//
// Files are stored as Blobs in IndexedDB together with their destination
// metadata, then replayed (storage upload + patient_documents insert) as soon
// as connectivity returns. Every entry carries a client-generated row id and a
// stable storage path, so replays are idempotent.

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { supabase } from "@/integrations/supabase/client";
import { trackSync } from "@/lib/sync-audit";

const DB_NAME = "doctorsx-upload-outbox";
const DB_VERSION = 1;
const STORE = "pending_uploads";
const BUCKET = "patient-documents";

export interface PendingUpload {
  key: string;
  /** client-generated uuid used as patient_documents.id */
  rowId: string;
  userId: string;
  patientId: string;
  name: string;
  fileType: string;
  fileSize: number;
  category: string;
  notes: string;
  filePath: string;
  blob: Blob;
  queued_at: number;
  seq: number;
  attempts: number;
  next_attempt_at: number;
  last_error?: string;
}

interface UploadSchema extends DBSchema {
  pending_uploads: {
    key: string;
    value: PendingUpload;
    indexes: { by_seq: number };
  };
}

let dbPromise: Promise<IDBPDatabase<UploadSchema>> | null = null;

function getDb() {
  if (!dbPromise) {
    dbPromise = openDB<UploadSchema>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: "key" });
          store.createIndex("by_seq", "seq");
        }
      },
    });
  }
  return dbPromise;
}

function uuid() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* ------------------------------------------------------------------ */
/* Subscriptions                                                       */
/* ------------------------------------------------------------------ */

type Listener = (items: PendingUpload[]) => void;
const listeners = new Set<Listener>();
const channel =
  typeof BroadcastChannel !== "undefined"
    ? new BroadcastChannel("doctorsx-upload-outbox")
    : null;

export async function listPendingUploads(): Promise<PendingUpload[]> {
  const db = await getDb();
  const all = await db.getAll(STORE);
  return all.sort((a, b) => a.seq - b.seq);
}

async function notify() {
  const items = await listPendingUploads();
  listeners.forEach((l) => l(items));
}

async function emit() {
  await notify();
  channel?.postMessage({ type: "changed" });
}

channel?.addEventListener("message", () => void notify());

export function subscribeUploadOutbox(fn: Listener): () => void {
  listeners.add(fn);
  void listPendingUploads().then(fn);
  return () => {
    listeners.delete(fn);
  };
}

export async function countPendingUploads() {
  const db = await getDb();
  return db.count(STORE);
}

export async function removePendingUpload(key: string) {
  const db = await getDb();
  await db.delete(STORE, key);
  await emit();
}

/* ------------------------------------------------------------------ */
/* Queueing                                                            */
/* ------------------------------------------------------------------ */

export interface UploadRequest {
  file: File | Blob;
  name: string;
  userId: string;
  patientId: string;
  category: string;
  notes: string;
}

function buildEntry(req: UploadRequest, seq: number): PendingUpload {
  const rowId = uuid();
  const safeName = req.name.replace(/[^\w.\-]+/g, "_");
  return {
    key: uuid(),
    rowId,
    userId: req.userId,
    patientId: req.patientId,
    name: req.name,
    fileType: (req.file as File).type || "application/octet-stream",
    fileSize: req.file.size,
    category: req.category,
    notes: req.notes,
    filePath: `${req.userId}/${req.patientId}/${Date.now()}_${rowId.slice(0, 8)}_${safeName}`,
    blob: req.file,
    queued_at: Date.now(),
    seq,
    attempts: 0,
    next_attempt_at: 0,
  };
}

async function nextSeq(db: IDBPDatabase<UploadSchema>) {
  const all = await db.getAll(STORE);
  return all.reduce((max, r) => Math.max(max, r.seq), 0) + 1;
}

export async function queueUpload(req: UploadRequest): Promise<PendingUpload> {
  const db = await getDb();
  const entry = buildEntry(req, await nextSeq(db));
  await db.put(STORE, entry);
  trackSync({ source: "upload", ref: entry.key, status: "queued", table: (entry as any).table, label: (entry as any).fileName ?? (entry as any).name ?? "File upload" });
  await emit();
  void requestBackgroundSync();
  return entry;
}

/* ------------------------------------------------------------------ */
/* Upload / replay                                                     */
/* ------------------------------------------------------------------ */

function isNetworkError(err: unknown) {
  const msg = String((err as { message?: string })?.message ?? err ?? "").toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("networkerror") ||
    msg.includes("load failed") ||
    msg.includes("timeout") ||
    msg.includes("offline")
  );
}

function isDuplicate(err: { code?: string; message?: string } | null) {
  if (!err) return false;
  const msg = String(err.message ?? "").toLowerCase();
  return err.code === "23505" || msg.includes("duplicate") || msg.includes("already exists");
}

/** Push one entry to storage + database. Throws on real failures. */
async function pushUpload(entry: PendingUpload) {
  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(entry.filePath, entry.blob, {
      contentType: entry.fileType,
      upsert: true,
    });
  if (uploadError && !isDuplicate(uploadError)) throw uploadError;

  const { error: dbError } = await supabase.from("patient_documents").insert({
    id: entry.rowId,
    user_id: entry.userId,
    patient_id: entry.patientId,
    name: entry.name,
    file_path: entry.filePath,
    file_type: entry.fileType,
    file_size: entry.fileSize,
    category: entry.category,
    notes: entry.notes,
  });
  if (dbError && !isDuplicate(dbError)) throw dbError;
}

/**
 * Upload now when online, otherwise queue the file in IndexedDB.
 * Returns `{ queued: true }` when the upload was deferred.
 */
export async function uploadWithOutbox(
  req: UploadRequest,
): Promise<{ queued: boolean; rowId: string }> {
  const db = await getDb();
  const entry = buildEntry(req, await nextSeq(db));
  const offline = typeof navigator !== "undefined" && !navigator.onLine;

  if (offline) {
    await db.put(STORE, entry);
    await emit();
    void requestBackgroundSync();
    return { queued: true, rowId: entry.rowId };
  }

  try {
    await pushUpload(entry);
    return { queued: false, rowId: entry.rowId };
  } catch (err) {
    if (isNetworkError(err)) {
      await db.put(STORE, entry);
      await emit();
      void requestBackgroundSync();
      return { queued: true, rowId: entry.rowId };
    }
    throw err;
  }
}

export interface UploadSyncState {
  syncing: boolean;
  total: number;
  done: number;
  lastSyncedAt: number | null;
  lastError: string | null;
}

let state: UploadSyncState = {
  syncing: false,
  total: 0,
  done: 0,
  lastSyncedAt: null,
  lastError: null,
};
const stateListeners = new Set<(s: UploadSyncState) => void>();

export function getUploadSyncState() {
  return state;
}

export function subscribeUploadSyncState(fn: (s: UploadSyncState) => void) {
  stateListeners.add(fn);
  fn(state);
  return () => {
    stateListeners.delete(fn);
  };
}

function setState(patch: Partial<UploadSyncState>) {
  state = { ...state, ...patch };
  stateListeners.forEach((l) => l(state));
}

const MAX_ATTEMPTS = 8;
function backoffMs(attempts: number) {
  return Math.min(60_000, 1000 * 2 ** attempts) + Math.random() * 500;
}

let running: Promise<{ synced: number; failed: number }> | null = null;

/** Replay all queued uploads in order. Safe to call repeatedly. */
export function syncUploadOutbox() {
  if (running) return running;
  running = (async () => {
    let synced = 0;
    let failed = 0;
    const db = await getDb();
    const items = await listPendingUploads();
    if (items.length === 0) {
      setState({ syncing: false, total: 0, done: 0 });
      return { synced, failed };
    }
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      return { synced, failed: items.length };
    }

    setState({ syncing: true, total: items.length, done: 0, lastError: null });

    for (const item of items) {
      if (item.next_attempt_at > Date.now()) {
        failed += 1;
        continue;
      }
      try {
        await pushUpload(item);
        await db.delete(STORE, item.key);
        trackSync({ source: "upload", ref: item.key, status: "synced", label: (item as any).fileName ?? (item as any).name ?? "File upload", attempt: item.attempts + 1 });
        synced += 1;
      } catch (err) {
        failed += 1;
        const attempts = item.attempts + 1;
        const message = String((err as { message?: string })?.message ?? err);
        if (attempts >= MAX_ATTEMPTS && !isNetworkError(err)) {
          await db.delete(STORE, item.key);
          trackSync({ source: "upload", ref: item.key, status: "dropped", label: (item as any).fileName ?? (item as any).name ?? "File upload", attempt: attempts, error: message });
        } else {
          trackSync({ source: "upload", ref: item.key, status: "retry", label: (item as any).fileName ?? (item as any).name ?? "File upload", attempt: attempts, error: message });
          await db.put(STORE, {
            ...item,
            attempts,
            last_error: message,
            next_attempt_at: Date.now() + backoffMs(attempts),
          });
        }
        setState({ lastError: message });
        if (isNetworkError(err)) break;
      }
      setState({ done: synced });
    }

    setState({
      syncing: false,
      lastSyncedAt: synced > 0 ? Date.now() : state.lastSyncedAt,
    });
    await emit();
    return { synced, failed };
  })().finally(() => {
    running = null;
  });
  return running;
}

async function requestBackgroundSync() {
  try {
    if (!("serviceWorker" in navigator)) return;
    const reg = (await navigator.serviceWorker.ready) as ServiceWorkerRegistration & {
      sync?: { register: (tag: string) => Promise<void> };
    };
    await reg.sync?.register("outbox-drain");
  } catch {
    /* Background Sync unsupported (iOS) — online listener handles it */
  }
}

let installed = false;

/** Install auto-sync triggers: reconnect, focus, periodic retry. Idempotent. */
export function initUploadOutbox(onSynced?: (r: { synced: number; failed: number }) => void) {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const run = async () => {
    if (!navigator.onLine) return;
    const r = await syncUploadOutbox();
    if (r.synced > 0) onSynced?.(r);
  };

  window.addEventListener("online", () => void run());
  window.addEventListener("focus", () => void run());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void run();
  });
  navigator.serviceWorker?.addEventListener?.("message", (event: MessageEvent) => {
    if ((event.data as { type?: string })?.type === "drain-outbox") void run();
  });
  setInterval(() => void run(), 30_000);

  void run();
}

/** Local object URL for previewing a queued (not yet uploaded) file. */
export function pendingUploadPreviewUrl(item: PendingUpload) {
  return URL.createObjectURL(item.blob);
}
